/**
 * OnboardingControls - Navigation buttons for the tour
 *
 * Provides Next, Back, Skip, and Finish buttons.
 * Layout adapts based on position in the tour.
 * For interactive steps, shows instruction text instead of Next button.
 */

import React from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, PartyPopper, MousePointerClick } from 'lucide-react';

interface OnboardingControlsProps {
  isFirstStep: boolean;
  isLastStep: boolean;
  interactive?: boolean;
  onNext: () => void;
  onPrev: () => void;
  onDismiss: () => void;
}

const OnboardingControls: React.FC<OnboardingControlsProps> = ({
  isFirstStep,
  isLastStep,
  interactive,
  onNext,
  onPrev,
  onDismiss,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between pt-1">
      {/* Left: Skip or Back */}
      <div>
        {isFirstStep ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="text-xs text-muted-foreground hover:text-foreground h-8 px-2"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {t('onboarding.controls.skip')}
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={onPrev}
            className="text-xs text-muted-foreground hover:text-foreground h-8 px-2"
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />
            {t('onboarding.controls.back')}
          </Button>
        )}
      </div>

      {/* Right: Interactive instruction, Next, or Finish */}
      <div>
        {interactive ? (
          <span className="flex items-center gap-1.5 text-xs text-primary font-medium h-8 px-2">
            <MousePointerClick className="h-3.5 w-3.5" />
            {t('onboarding.controls.clickToContinue')}
          </span>
        ) : isLastStep ? (
          <Button
            size="sm"
            onClick={onNext}
            className="h-8 px-3 text-xs"
          >
            <PartyPopper className="h-3.5 w-3.5 mr-1" />
            {t('onboarding.controls.finish')}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={onNext}
            className="h-8 px-3 text-xs"
          >
            {isFirstStep
              ? t('onboarding.controls.startTour')
              : t('onboarding.controls.next')
            }
            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default OnboardingControls;
